import { useEffect, useState } from 'react'

/**
 * ADR-0155 WARNING E (Gate-5 re-review) — a generic debounce for exactly ONE
 * consumer today: `StagingPage.tsx`'s `searchDisclosureMessage`. There is no
 * `debounce`/`setTimeout` anywhere on the Staging page before this file, so
 * the single consolidated `aria-live="polite"` region (D4's CRITICAL 3 fix)
 * still received one content change per keystroke — typing a 7-character
 * query queued up to 7 announcements. Consolidating to one region helped
 * (most screen readers coalesce or interrupt successive updates to the SAME
 * region) but did not remove the queue; this closes that.
 *
 * Deliberately narrow, per the WARNING's own scope: this debounces the
 * ANNOUNCEMENT value only. It must never be reached for from the filtering
 * path — `searchQuery` itself, and everything `staging-part-search.ts`
 * derives from it for RENDERING, stays instant (R5's own "no query, no
 * narrowing" baseline already assumes synchronous filtering; debouncing that
 * would make the visible rows lag behind what the user typed, which no ADR
 * asked for).
 *
 * `immediateWhen` exists for exactly one case: clearing the query. A user
 * clicking "Clear search" has taken a deliberate, discrete action — not
 * produced a burst of keystrokes — so the debounce that protects against
 * per-keystroke announcement noise has nothing to protect here, and holding
 * the stale "Showing X of Y" text on screen for another `delayMs` after the
 * rows have already reset to the full set would itself be a disclosure lag
 * ADR-0155 D4 does not tolerate (the live region must reflect the CURRENT
 * state, not a queued one, the moment the state that matters — "is a query
 * active at all?" — changes to false).
 */
export function useDebouncedValue<T>(
  value: T,
  delayMs: number,
  options: { readonly immediateWhen?: (value: T) => boolean } = {},
): T {
  const [debounced, setDebounced] = useState(value)

  // biome-ignore lint/correctness/useExhaustiveDependencies: `options.immediateWhen` is expected to be a stable predicate identity that callers pass inline (not tracked) — re-running the effect only on `value`/`delayMs` mirrors every other debounce hook in this codebase (see `use-add-parts-search.ts`).
  useEffect(() => {
    if (options.immediateWhen?.(value)) {
      setDebounced(value)
      return
    }
    const timer = setTimeout(() => setDebounced(value), delayMs)
    // Cleanup runs on every dependency change AND on unmount — either way,
    // no pending `setTimeout` outlives this effect instance.
    return () => clearTimeout(timer)
  }, [value, delayMs])

  return debounced
}
